/**
 * Common v0.9
 */

// [祝日判定] japanese-holidaysライブラリを使用
import * as Holidays from 'japanese-holidays';

/**
 * [日付の日本語化]
 * 土曜は青、日曜・祝日は赤、それ以外は黒で色付けしたHTMLを返します。
 * 
 * @param {string|Date} dateStr 日付（例: "2025-06-22"）
 * @returns {string} HTML文字列
 */
export function getDateJp(dateStr) {
    // 日付オブジェクトに変換
    const date = new Date(dateStr);

    // 年・月・日を2桁で取得
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    // 曜日を日本語で取得
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    const weekday = weekdays[date.getDay()];

    // 祝日判定
    const isHoliday = Holidays.isHoliday(date);

    // 色の決定
    let color = '';
    if (date.getDay() === 0 || isHoliday) {
        color = 'text-danger';   // 日曜または祝日
    } else if (date.getDay() === 6) {
        color = 'text-primary';  // 土曜
    }

    // フォーマット
    const formatted = `${year}年${month}月${day}日（${weekday}）`;

    // HTMLで返す
    return `<span class="${color}">${formatted}</span>`;
}

/**
 * [時刻の日本語化] 指定した日付から時間をHH:MM形式で返す関数です。
 * 
 * @param {string|Date} dateStr 日付（例: "2025-06-22"）
 * @returns {string} 時間（HH:MM形式）
 */
export function getTimeJp(dateStr) {
    // 日付を日本語表記で取得
    let $line = getDateJp(dateStr);

    // 時間を取得
    const date = new Date(dateStr);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${line} ${hours}:${minutes}`;
}

/**
 * [機関の日本語化] 開始～終了
 * 開始・終了日時を受け取り、「HH:MM ～ HH:MM」または「HH:MM ～」の形式で返します。
 * 
 * @param {string|Date} startDate 開始日時（任意のフォーマットでOK）
 * @param {string|Date|null} endDate 終了日時（省略可）
 * @returns {string} 整形済みの時間帯文字列
 */
export function getStartEnd(startDate, endDate = null) {
    // 開始・終了をDateオブジェクトに変換
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;

    // 時刻を「HH:MM」形式で取得
    const formatTime = date =>
        date
            ? date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', hour12: false })
            : '';

    // 開始～終了
    let result;
    if (end) {
        // 同じ日かどうかは年月日で比較
        const isSameDay =
            start.getFullYear() === end.getFullYear() &&
            start.getMonth() === end.getMonth() &&
            start.getDate() === end.getDate();

        // 同日・異日どちらも同じフォーマット
        result = `${formatTime(start)} ～ ${formatTime(end)}`;
    } else {
        result = `${formatTime(start)} ～`;
    }

    return result;
}

/*
// [位置情報] (Laravel側でサポート)
export async function getJapanesePrefecture(client_ip) {
    try {
        // GeoJS APIからIPと地域情報を取得
        const response = await fetch('https://get.geojs.io/v1/ip/geo.json?ip=' + client_ip);
        const data = await response.json();

        // 英語の地域名を取得
        const englishRegion = data.region;

        // 英語→日本語の都道府県対応表
        const prefectureMap = {
            'Aichi': '愛知県',
            'Akita': '秋田県',
            'Aomori': '青森県',
            'Chiba': '千葉県',
            'Ehime': '愛媛県',
            'Fukui': '福井県',
            'Fukuoka': '福岡県',
            'Fukushima': '福島県',
            'Gifu': '岐阜県',
            'Gunma': '群馬県',
            'Hiroshima': '広島県',
            'Hokkaido': '北海道',
            'Hyogo': '兵庫県',
            'Ibaraki': '茨城県',
            'Ishikawa': '石川県',
            'Iwate': '岩手県',
            'Kagawa': '香川県',
            'Kagoshima': '鹿児島県',
            'Kanagawa': '神奈川県',
            'Kochi': '高知県',
            'Kumamoto': '熊本県',
            'Kyoto': '京都府',
            'Mie': '三重県',
            'Miyagi': '宮城県',
            'Miyazaki': '宮崎県',
            'Nagano': '長野県',
            'Nagasaki': '長崎県',
            'Nara': '奈良県',
            'Niigata': '新潟県',
            'Oita': '大分県',
            'Okayama': '岡山県',
            'Okinawa': '沖縄県',
            'Osaka': '大阪府',
            'Saga': '佐賀県',
            'Saitama': '埼玉県',
            'Shiga': '滋賀県',
            'Shimane': '島根県',
            'Shizuoka': '静岡県',
            'Tochigi': '栃木県',
            'Tokushima': '徳島県',
            'Tokyo': '東京都',
            'Tottori': '鳥取県',
            'Toyama': '富山県',
            'Wakayama': '和歌山県',
            'Yamagata': '山形県',
            'Yamaguchi': '山口県',
            'Yamanashi': '山梨県'
        };

        // 英語の地域名から日本語の都道府県名に変換
        const japanesePrefecture = prefectureMap[englishRegion] || '東京都';

        return japanesePrefecture;
    } catch (error) {
        console.error('都道府県の取得に失敗しました:', error);
        return '東京都';
    }
}
*/

// [Toast]
export function show_toast(title, message, is_ok = 1) {
    // Unique ID
    const now = Date.now();

    // Snippet
    const snip = '<div id="'
        + now
        + '" class="toast" role="alert" aria-live="assertive" aria-atomic="true"><div class="toast-header"><span class="'
        + (is_ok ? 'text-success' : 'text-danger')
        + '">■</span><strong class="me-auto">'
        + title
        + '</strong><small></small><button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button></div><div class="toast-body">'
        + message
        + '</div></div>';

    // Show Toast
    const container = document.querySelector('div.toast-container');
    if (container) {
        container.insertAdjacentHTML('afterbegin', snip);
        const toastLive = document.getElementById(now);
        const toast = bootstrap.Toast.getOrCreateInstance(toastLive);
        toast.show();
    }
}

/**
 * [お気に入り] マークをDBに合わせて更新する関数（Vanilla JS版）
 * 
 * @param {Array} reserves お気に入り対象のID配列
 */
export function get_favorite(reserves) {
    // fetch APIでPUTリクエストを送信
    fetch('/private/ajax/get_favorite', {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({ reserves: reserves }),
    })
        .then(response => response.json())
        .then(data => {
            data.forEach(function (reserve) {
                // マークの書き換え
                const elem = document.getElementById(reserve.id);
                if (elem) {
                    const favIcon = elem.querySelector('.favorite i');
                    if (favIcon) {
                        if (reserve.is_checked) {
                            favIcon.classList.remove('bi-heart');
                            favIcon.classList.add('bi-heart-fill');
                        }
                    }
                    // 人数表示
                    const numElem = elem.querySelector('.num_favorite');
                    if (numElem) {
                        numElem.textContent = '+' + reserve.num;
                    }
                }
            });
        })
        .catch(error => {
            // エラー時の処理（必要に応じて実装）
            // console.error('お気に入り取得エラー:', error);
        });
}

/**
 * [お気に入り] ブラウザのローカルストレージの内容を反映する関数（Vanilla JS版）
 * 
 * @param {string} target カテゴリ等
 */
export function chk_favorite(target) {
    // 「favorite」クラスを持つ全要素を取得
    let favorites = document.getElementsByClassName("favorite");
    for (let i = 0; i < favorites.length; i++) {
        favorites[i].addEventListener("click", () => {
            // 店舗・キャストのIDの取得（data-id属性から取得）
            let id = favorites[i].getAttribute('data-id');

            // アイコン要素を取得し、クラス名に"fill"が含まれているかで状態判定
            let icon = favorites[i].querySelector('i');
            let is_valid = icon && icon.className.indexOf("fill") >= 0 ? 0 : 1;

            // ローカルストレージからお気に入りID配列を取得
            let ids = localStorage.getItem('favorite');
            ids = (ids === null) ? [] : JSON.parse(ids);

            // 配列に追加または削除
            let index = ids.indexOf(id);
            if (is_valid) {
                (index < 0) && ids.push(id);
            } else {
                (index >= 0) && ids.splice(index, 1);
            }

            // ローカルストレージに保存
            localStorage.setItem('favorite', JSON.stringify(ids));

            // マークの反転および、ログイン時はサーバ側にも登録
            set_favorite(favorites[i], is_valid, target);
        });
    }
}

/**
 * [お気に入り] on/off （Vanilla JS版）
 * @param {HTMLElement} target クリックされた要素
 * @param {number} is_valid 1:お気に入り追加, 0:削除
 * @param {string} category カテゴリ等
 */
export function set_favorite(target, is_valid, category) {
    // data-id属性からIDを取得
    const target_id = target.getAttribute('data-id');

    // CSRFトークンを取得
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    // fetch APIでPUTリクエストを送信
    fetch('/api/set_favorite', {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
            target_id: target_id,
            action: is_valid,
            category: category,
        }),
    })
        .then(response => response.json())
        .then(data => {
            // マークの書き換え
            const icon = target.querySelector('i');
            if (icon) {
                if (is_valid) {
                    icon.classList.remove('bi-heart');
                    icon.classList.add('bi-heart-fill');
                } else {
                    icon.classList.remove('bi-heart-fill');
                    icon.classList.add('bi-heart');
                }
            }
        })
        .catch(error => {
            // エラー時の処理（必要に応じて実装）
            // console.error('お気に入り登録エラー:', error);
        });

    return true;
}

